import { useState } from 'react'
import { useSession } from '@/features/auth/hooks/useSession'
import { useEscapeKey } from '@/common/hooks/useEscapeKey'
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick'
import { PlanPicker } from './PlanPicker'
import { ExportMyDataButton } from './ExportMyDataButton'
import './PlanChoiceModal.css'

interface PlanChoiceModalProps {
  isOpen: boolean
  onClose: () => void
  context?: 'add_workspace' | 'upgrade'
  /**
   * Called with the new workspace id after a Free create (paid plans redirect
   * to Stripe). May be async — it is awaited before the modal closes so the
   * container fetch/select finishes and the user lands in the new workspace.
   * Only used for context="add_workspace".
   */
  onCreated?: (spaceId: string) => void | Promise<void>
  /**
   * Called when the user dismisses the upgrade prompt (upgrade context only).
   * Falls back to onClose if not provided.
   */
  onDismiss?: () => void
  /**
   * Auth token to pass to ExportMyDataButton (upgrade context only).
   * Falls back to the session token if not provided.
   */
  authToken?: string
}

/**
 * PlanChoice modal — a focused dialog (not a fullscreen gate) that renders the
 * shared `PlanPicker` inside the proven modal chrome. Supports two contexts:
 * - `add_workspace` (default): create a new workspace with a plan.
 * - `upgrade`: let the current workspace owner upgrade or dismiss (PDR 012 Phase 2).
 */
export function PlanChoiceModal({
  isOpen,
  onClose,
  context = 'add_workspace',
  onCreated,
  onDismiss,
  authToken,
}: PlanChoiceModalProps) {
  useEscapeKey(onClose, isOpen)
  const [modalRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30)
  const { token, profile, containers, containersLoaded } = useSession()
  const [error, setError] = useState<string | null>(null)

  if (!isOpen || !profile) return null

  const isUpgrade = context === 'upgrade'

  return (
    <div
      className="plan-choice-modal-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`plan-choice-modal${isUpgrade ? ' plan-choice-modal--upgrade' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="plan-choice-modal-header">
          <div>
            {isUpgrade ? (
              <>
                <h2 className="text-base font-semibold text-gray-900">Choose your plan</h2>
                <p className="mt-0.5 text-xs text-gray-500">Pick the plan that fits.</p>
              </>
            ) : (
              <>
                <h2 className="text-base font-semibold text-gray-900">Add a workspace</h2>
                <p className="mt-0.5 text-xs text-gray-500">Name it and pick a plan.</p>
              </>
            )}
          </div>
          <button className="plan-choice-modal-close" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="plan-choice-modal-body xv-scroll">
          {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
          {isUpgrade ? (
            <PlanPicker
              user={profile}
              authToken={token}
              onError={setError}
              context="upgrade"
              compact
              onDismiss={onDismiss ?? onClose}
            />
          ) : (
            <PlanPicker
              user={profile}
              authToken={token}
              onError={setError}
              context={context}
              compact
              containers={containers}
              containersLoading={!containersLoaded}
              onWorkspaceCreated={async (id) => {
                // Await the container fetch/select so the user lands in the new
                // workspace before the modal unmounts.
                await onCreated?.(id)
                onClose()
              }}
            />
          )}
        </div>
        {isUpgrade && (
          <footer className="plan-choice-modal-footer">
            <ExportMyDataButton authToken={authToken ?? token} />
          </footer>
        )}
      </div>
    </div>
  )
}
